import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { toast } from "sonner";
import {
  Search,
  Package,
  Plus,
  X,
  ArrowRightLeft,
  ChevronLeft,
  Boxes,
  Link2,
  Unlink,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import styles from "./Patrimonio.module.css";
import { useSessionExpirationCheck } from "../../hooks/useSessionExpirationCheck";

type TipoPatrimonio = "Unitario" | "Case";

type Patrimonio = {
  id: number;
  projeto: string;
  numero_patrimonio: string;
  nome: string;
  tipo_patrimonio: TipoPatrimonio;
  categoria: string | null;
  numero_serie: string | null;
  quantidade: number;
  status_atual: string;
  localizacao_atual: string | null;
  com_quem_esta: string | null;
  observacoes: string | null;
  id_patrimonio_pai: number | null;
  numero_patrimonio_pai?: string | null;
  nome_pai?: string | null;
  qtd_itens?: number;
  itens?: Patrimonio[];
  movimentacoes?: Movimentacao[];
};

type Movimentacao = {
  id: number;
  id_patrimonio: number;
  tipo_movimentacao: string;
  status_anterior: string | null;
  status_novo: string;
  quantidade_movimentada: number | null;
  localizacao_origem: string | null;
  localizacao_destino: string | null;
  retirado_por: string | null;
  com_quem_esta: string | null;
  id_grupo_anterior: number | null;
  numero_grupo_anterior: string | null;
  id_grupo_novo: number | null;
  numero_grupo_novo: string | null;
  data_movimentacao: string;
  data_prevista_retorno: string | null;
  observacoes: string | null;
  nome_usuario_registro: string | null;
};

type NovoItemForm = {
  numero_patrimonio: string;
  nome: string;
  categoria: string;
  numero_serie: string;
  quantidade: string;
  observacoes: string;
};

const PROJETOS = ["Monitora", "Inspeciona"];

const STATUS_OPCOES = [
  "Escritorio",
  "Com Terceiro",
  "Instalacao",
  "Manutencao",
  "Guardado",
  "Desativado",
];

const STATUS_LABEL: Record<string, string> = {
  Escritorio: "No escritório",
  Instalacao: "Instalacao",
  "Com Terceiro": "Com terceiro",
  Manutencao: "Em manutenção",
  Guardado: "Guardado",
  Desativado: "Desativado",
};

// Status que representam um lugar físico de verdade. Para os demais,
// "Localização atual" mostra o nome do status, e o endereço real fica
// guardado como "Localização anterior".
const STATUS_LOCALIZACAO_FISICA = new Set(["Escritorio", "Instalacao"]);

// Para status físicos, o badge mostra "{prefixo} {localização}" em vez do
// texto fixo do STATUS_LABEL, quando há localização cadastrada.
const PREFIXO_LOCALIZACAO: Record<string, string> = {
  Escritorio: "Em/No(a)",
  Instalacao: "Instalado em/no(a)",
};

function labelStatusComLocal(
  status: string,
  localizacao?: string | null,
): string {
  const prefixo = PREFIXO_LOCALIZACAO[status];
  if (prefixo && localizacao) return `${prefixo} ${localizacao}`;
  return STATUS_LABEL[status] ?? status;
}

const PROJETO_CLASSE: Record<string, string> = {
  Monitora: styles.tagMonitora,
  Inspeciona: styles.tagInspeciona,
};

// Define, por tipo de movimentação, o label do campo "responsável".
// null = o campo não é exibido para esse tipo.
const RESPONSAVEL_LABEL: Record<string, string | null> = {
  Retirada: "Retirado por",
  Realocacao: "Retirado por",
  Instalacao: "Instalado por",
  Devolucao: "Devolvido por",
  Manutencao: null,
  Guarda: null,
  Desativacao: null,
};

const RESPONSAVEL_PLACEHOLDER: Record<string, string> = {
  Retirada: "Quem foi buscar o equipamento",
  Realocacao: "Quem foi buscar o equipamento",
  Instalacao: "Quem instalou o equipamento",
  Devolucao: "Quem devolveu o equipamento",
};

const ITEM_VAZIO: NovoItemForm = {
  numero_patrimonio: "",
  nome: "",
  categoria: "",
  numero_serie: "",
  quantidade: "1",
  observacoes: "",
};

const StatusBadge: React.FC<{
  status: string;
  localizacao?: string | null;
}> = ({ status, localizacao }) => (
  <span
    className={`${styles.badge} ${styles["badge_" + status.replace(" ", "_")]}`}
  >
    <span className={styles.dot} />
    {labelStatusComLocal(status, localizacao)}
  </span>
);

const TipoBadge: React.FC<{ tipo: TipoPatrimonio }> = ({ tipo }) => (
  <span
    className={`${styles.tagTipo} ${tipo === "Case" ? styles.tagTipoCase : styles.tagTipoUnitario}`}
  >
    {tipo === "Case" ? <Boxes size={12} /> : <Package size={12} />}
    {tipo === "Case" ? "Case" : "Avulso"}
  </span>
);

function descreverMovimentacaoGrupo(mov: Movimentacao): string | null {
  if (mov.id_grupo_novo && !mov.id_grupo_anterior) {
    return `Instalado em ${mov.numero_grupo_novo}`;
  }
  if (mov.id_grupo_anterior && !mov.id_grupo_novo) {
    return `Removido de ${mov.numero_grupo_anterior}`;
  }
  if (
    mov.id_grupo_anterior &&
    mov.id_grupo_novo &&
    mov.id_grupo_anterior !== mov.id_grupo_novo
  ) {
    return `Movido de ${mov.numero_grupo_anterior} para ${mov.numero_grupo_novo}`;
  }
  return null;
}

const Patrimonios: React.FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const sessionExpiration = useSelector(
    (state: RootState) => state.auth.SessionExpiration,
  );

  const navigate = useNavigate();
  useSessionExpirationCheck(sessionExpiration, token);

  const [lista, setLista] = useState<Patrimonio[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [termo, setTermo] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("");
  const [projetoFiltro, setProjetoFiltro] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("");

  // Modal — Novo Patrimônio
  const [modalNovoShow, setModalNovoShow] = useState(false);
  const [novoTipo, setNovoTipo] = useState<TipoPatrimonio | null>(null);
  const [novoForm, setNovoForm] = useState({
    projeto: "Monitora",
    numero_patrimonio: "",
    nome: "",
    categoria: "",
    numero_serie: "",
    quantidade: "1",
    localizacao_atual: "",
    observacoes: "",
  });
  const [novoItens, setNovoItens] = useState<NovoItemForm[]>([
    { ...ITEM_VAZIO },
  ]);

  // Pilha de navegação do detalhe: permite entrar em uma case, clicar num
  // item dela, ver o detalhe do item, e voltar sem perder o contexto.
  const [pilha, setPilha] = useState<Patrimonio[]>([]);
  const patrimonioAtual = pilha.length > 0 ? pilha[pilha.length - 1] : null;

  const [modalMovShow, setModalMovShow] = useState(false);
  const [novaMov, setNovaMov] = useState({
    tipo_movimentacao: "Retirada",
    status_novo: "Com Terceiro",
    localizacao_destino: "",
    retirado_por: "",
    com_quem_esta: "",
    data_prevista_retorno: "",
    observacoes: "",
  });

  const [modalAssociarShow, setModalAssociarShow] = useState(false);
  const [casesDisponiveis, setCasesDisponiveis] = useState<Patrimonio[]>([]);
  const [caseSelecionada, setCaseSelecionada] = useState("");
  const [obsAssociar, setObsAssociar] = useState("");

  const buscar = useCallback(async () => {
    setCarregando(true);
    try {
      const params = new URLSearchParams();
      if (termo) params.append("termo", termo);
      if (statusFiltro) params.append("status", statusFiltro);
      if (projetoFiltro) params.append("projeto", projetoFiltro);
      if (tipoFiltro) params.append("tipo", tipoFiltro);

      const resp = await fetch(
        `${process.env.REACT_APP_API}/api/patrimonio?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error("Erro ao buscar patrimônios.");
      setLista(await resp.json());
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [termo, statusFiltro, projetoFiltro, tipoFiltro, token]);

  useEffect(() => {
    buscar();
    document.title = "Patrimônios";
  }, [buscar]);

  async function abrirDetalhe(id: number, nova = true) {
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API}/api/patrimonio/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!resp.ok) throw new Error("Erro ao carregar patrimônio.");
      const dados: Patrimonio = await resp.json();
      setPilha((atual) => (nova ? [dados] : [...atual, dados]));
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  function voltarDetalhe() {
    setPilha((atual) => atual.slice(0, -1));
  }

  function fecharDetalhe() {
    setPilha([]);
  }

  async function atualizarTopoPilha() {
    if (!patrimonioAtual) return;
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API}/api/patrimonio/${patrimonioAtual.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) return;
      const dados = await resp.json();
      setPilha((atual) => [...atual.slice(0, -1), dados]);
    } catch {
      // silencioso — próxima busca manual resolve
    }
  }

  function fecharModalNovo() {
    setModalNovoShow(false);
    setNovoTipo(null);
    setNovoForm({
      projeto: "Monitora",
      numero_patrimonio: "",
      nome: "",
      categoria: "",
      numero_serie: "",
      quantidade: "1",
      localizacao_atual: "",
      observacoes: "",
    });
    setNovoItens([{ ...ITEM_VAZIO }]);
  }

  function atualizarItem(
    index: number,
    campo: keyof NovoItemForm,
    valor: string,
  ) {
    setNovoItens((atual) =>
      atual.map((item, i) =>
        i === index ? { ...item, [campo]: valor } : item,
      ),
    );
  }

  function adicionarLinhaItem() {
    setNovoItens((atual) => [...atual, { ...ITEM_VAZIO }]);
  }

  function removerLinhaItem(index: number) {
    setNovoItens((atual) => atual.filter((_, i) => i !== index));
  }

  async function criarPatrimonio() {
    if (!novoTipo) return;
    if (!novoForm.numero_patrimonio || !novoForm.nome) {
      toast.error("Número do patrimônio e nome são obrigatórios.");
      return;
    }
    if (
      novoTipo === "Case" &&
      novoItens.some((i) => !i.numero_patrimonio || !i.nome)
    ) {
      toast.error("Preencha número e nome de todos os itens da case.");
      return;
    }

    try {
      const payload = {
        projeto: novoForm.projeto,
        numero_patrimonio: novoForm.numero_patrimonio,
        nome: novoForm.nome,
        tipo_patrimonio: novoTipo,
        categoria: novoForm.categoria || null,
        numero_serie:
          novoTipo === "Unitario" ? novoForm.numero_serie || null : null,
        quantidade:
          novoTipo === "Unitario" ? Number(novoForm.quantidade) || 1 : 1,
        localizacao_atual: novoForm.localizacao_atual || null,
        observacoes: novoForm.observacoes || null,
        itens:
          novoTipo === "Case"
            ? novoItens.map((i) => ({
                numero_patrimonio: i.numero_patrimonio,
                nome: i.nome,
                categoria: i.categoria || null,
                numero_serie: i.numero_serie || null,
                quantidade: Number(i.quantidade) || 1,
                observacoes: i.observacoes || null,
              }))
            : [],
      };

      const resp = await fetch(`${process.env.REACT_APP_API}/api/patrimonio`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const mensagemErro = await resp.text();
        throw new Error(mensagemErro || "Erro ao criar patrimônio.");
      }
      toast.success(
        novoTipo === "Case"
          ? "Case criada com os itens."
          : "Patrimônio criado com sucesso.",
      );
      fecharModalNovo();
      buscar();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  function abrirModalMov() {
    if (!patrimonioAtual) return;
    setNovaMov({
      tipo_movimentacao: "Retirada",
      status_novo: "Com Terceiro",
      localizacao_destino: "",
      retirado_por: "",
      com_quem_esta: "",
      data_prevista_retorno: "",
      observacoes: "",
    });
    setModalMovShow(true);
  }

  async function registrarMovimentacao() {
    if (!patrimonioAtual) return;
    if (!novaMov.status_novo || !novaMov.tipo_movimentacao) {
      toast.error("Preencha o tipo e o novo status.");
      return;
    }
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API}/api/patrimonio/${patrimonioAtual.id}/movimentacoes`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            tipo_movimentacao: novaMov.tipo_movimentacao,
            status_novo: novaMov.status_novo,
            localizacao_destino: novaMov.localizacao_destino || null,
            retirado_por: novaMov.retirado_por || null,
            com_quem_esta: novaMov.com_quem_esta || null,
            observacoes: novaMov.observacoes || null,
            data_prevista_retorno: novaMov.data_prevista_retorno || null,
          }),
        },
      );
      if (!resp.ok) throw new Error("Erro ao registrar movimentação.");
      toast.success("Movimentação registrada.");
      setModalMovShow(false);
      await atualizarTopoPilha();
      buscar();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function abrirAssociar() {
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API}/api/patrimonio?tipo=Case`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!resp.ok) throw new Error("Erro ao carregar cases.");
      const dados: Patrimonio[] = await resp.json();
      setCasesDisponiveis(dados.filter((c) => c.id !== patrimonioAtual?.id));
      setCaseSelecionada("");
      setObsAssociar("");
      setModalAssociarShow(true);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function confirmarAssociar() {
    if (!patrimonioAtual || !caseSelecionada) {
      toast.error("Selecione uma case de destino.");
      return;
    }
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API}/api/patrimonio/${patrimonioAtual.id}/associar`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            id_case: Number(caseSelecionada),
            observacoes: obsAssociar || null,
          }),
        },
      );
      if (!resp.ok) throw new Error("Erro ao associar patrimônio.");
      toast.success("Item adicionado à case.");
      setModalAssociarShow(false);
      await atualizarTopoPilha();
      buscar();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function desassociar() {
    if (!patrimonioAtual) return;
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API}/api/patrimonio/${patrimonioAtual.id}/desassociar`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ observacoes: null }),
        },
      );
      if (!resp.ok) throw new Error("Erro ao remover do grupo.");
      toast.success("Item removido do grupo — voltou para os avulsos.");
      await atualizarTopoPilha();
      buscar();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          className={styles.backButton}
          onClick={() => navigate(-1)}
          aria-label="Voltar"
        >
          <ChevronLeft size={22} />
        </button>
        <h2 className={styles.title}>Patrimônios</h2>
        <button
          className={styles.btnPrimary}
          onClick={() => setModalNovoShow(true)}
        >
          <Plus size={16} /> Novo Patrimônio
        </button>
      </header>

      <div className={styles.filtros}>
        <div className={styles.campoBusca}>
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar por número, nome ou localização..."
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && buscar()}
          />
        </div>

        <select
          value={projetoFiltro}
          onChange={(e) => setProjetoFiltro(e.target.value)}
        >
          <option value="">Todos os projetos</option>
          {PROJETOS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>

        <select
          value={tipoFiltro}
          onChange={(e) => setTipoFiltro(e.target.value)}
        >
          <option value="">Cases e avulsos</option>
          <option value="Case">Só cases</option>
          <option value="Unitario">Só avulsos</option>
        </select>

        <select
          value={statusFiltro}
          onChange={(e) => setStatusFiltro(e.target.value)}
        >
          <option value="">Todos os status</option>
          {STATUS_OPCOES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>

        <button className={styles.btnOutline} onClick={buscar}>
          Filtrar
        </button>
      </div>

      {carregando ? (
        <div className={styles.estadoVazio}>Carregando...</div>
      ) : lista.length === 0 ? (
        <div className={styles.estadoVazio}>
          <Package size={32} />
          <p>Nenhum patrimônio encontrado.</p>
          <p className={styles.textoMuted}>
            Clique em "Novo Patrimônio" para cadastrar um item avulso ou uma
            case com equipamentos.
          </p>
        </div>
      ) : (
        <div className={styles.grid}>
          {lista.map((p) => (
            <div
              key={p.id}
              className={styles.card}
              onClick={() => abrirDetalhe(p.id)}
            >
              <div className={styles.cardTop}>
                <span className={styles.numeroPatrimonio}>
                  {p.numero_patrimonio}
                </span>
                <span
                  className={`${styles.tagProjeto} ${PROJETO_CLASSE[p.projeto] ?? ""}`}
                >
                  {p.projeto}
                </span>
              </div>

              <h3 className={styles.cardNome}>{p.nome}</h3>

              <div className={styles.cardTagsLinha}>
                <TipoBadge tipo={p.tipo_patrimonio} />
                {p.categoria && (
                  <span className={styles.cardCategoria}>{p.categoria}</span>
                )}
              </div>

              <div className={styles.cardStatusLinha}>
                <StatusBadge
                  status={p.status_atual}
                  localizacao={p.localizacao_atual}
                />
              </div>

              <div className={styles.cardInfo}>
                <div>
                  <span className={styles.infoLabel}>Localização</span>
                  <span className={styles.infoValue}>
                    {STATUS_LOCALIZACAO_FISICA.has(p.status_atual)
                      ? p.localizacao_atual || "—"
                      : (STATUS_LABEL[p.status_atual] ?? p.status_atual)}
                  </span>
                </div>
                <div>
                  <span className={styles.infoLabel}>Com quem está</span>
                  <span className={styles.infoValue}>
                    {p.com_quem_esta || "—"}
                  </span>
                </div>
              </div>

              {p.tipo_patrimonio === "Case" && (
                <div className={styles.cardItensResumo}>
                  {p.qtd_itens ?? 0} {p.qtd_itens === 1 ? "item" : "itens"}{" "}
                  dentro desta case
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal — Novo Patrimônio */}
      {modalNovoShow && (
        <div className={styles.overlay} onClick={fecharModalNovo}>
          <div
            className={`${styles.modal} ${novoTipo === "Case" ? styles.modalGrande : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <header className={styles.modalHeader}>
              <h2>Novo Patrimônio</h2>
              <button
                className={styles.btnCloseModal}
                onClick={fecharModalNovo}
              >
                <X size={20} />
              </button>
            </header>

            {!novoTipo ? (
              <div className={styles.modalBody}>
                <p className={styles.textoMuted}>
                  O que você está cadastrando?
                </p>
                <div className={styles.tipoEscolha}>
                  <button
                    className={styles.tipoCard}
                    onClick={() => setNovoTipo("Unitario")}
                  >
                    <Package size={26} />
                    <strong>Item avulso</strong>
                    <span>Um equipamento único, sem outros itens dentro</span>
                  </button>
                  <button
                    className={styles.tipoCard}
                    onClick={() => setNovoTipo("Case")}
                  >
                    <Boxes size={26} />
                    <strong>Case com equipamentos</strong>
                    <span>Um case/kit que carrega vários itens dentro</span>
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className={styles.modalBody}>
                  <button
                    className={styles.linkVoltarTipo}
                    onClick={() => setNovoTipo(null)}
                  >
                    ← Trocar tipo
                  </button>

                  <label>Projeto *</label>
                  <select
                    value={novoForm.projeto}
                    onChange={(e) =>
                      setNovoForm((f) => ({ ...f, projeto: e.target.value }))
                    }
                  >
                    {PROJETOS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>

                  <label>
                    {novoTipo === "Case"
                      ? "Número da case *"
                      : "Número do patrimônio *"}
                  </label>
                  <input
                    value={novoForm.numero_patrimonio}
                    onChange={(e) =>
                      setNovoForm((f) => ({
                        ...f,
                        numero_patrimonio: e.target.value,
                      }))
                    }
                    placeholder="Ex: MON-00123"
                  />

                  <label>Nome / descrição *</label>
                  <input
                    value={novoForm.nome}
                    onChange={(e) =>
                      setNovoForm((f) => ({ ...f, nome: e.target.value }))
                    }
                    placeholder={
                      novoTipo === "Case"
                        ? "Ex: Case de campo #3"
                        : "Ex: Notebook Dell 14"
                    }
                  />

                  <label>Categoria</label>
                  <input
                    value={novoForm.categoria}
                    onChange={(e) =>
                      setNovoForm((f) => ({ ...f, categoria: e.target.value }))
                    }
                    placeholder="Ex: Notebook, Case, Sensor..."
                  />

                  {novoTipo === "Unitario" && (
                    <>
                      <label>Número de série</label>
                      <input
                        value={novoForm.numero_serie}
                        onChange={(e) =>
                          setNovoForm((f) => ({
                            ...f,
                            numero_serie: e.target.value,
                          }))
                        }
                      />

                      <label>Quantidade</label>
                      <input
                        type="number"
                        min={1}
                        value={novoForm.quantidade}
                        onChange={(e) =>
                          setNovoForm((f) => ({
                            ...f,
                            quantidade: e.target.value,
                          }))
                        }
                      />
                    </>
                  )}

                  <label>Localização atual</label>
                  <input
                    value={novoForm.localizacao_atual}
                    onChange={(e) =>
                      setNovoForm((f) => ({
                        ...f,
                        localizacao_atual: e.target.value,
                      }))
                    }
                    placeholder="Ex: Escritório - Sala TI"
                  />

                  <label>Observações</label>
                  <textarea
                    rows={2}
                    value={novoForm.observacoes}
                    onChange={(e) =>
                      setNovoForm((f) => ({
                        ...f,
                        observacoes: e.target.value,
                      }))
                    }
                  />

                  {novoTipo === "Case" && (
                    <div className={styles.itensLista}>
                      <div className={styles.itensListaTopo}>
                        <span className={styles.infoLabel}>
                          Itens dentro desta case
                        </span>
                        <button
                          type="button"
                          className={styles.btnAddItem}
                          onClick={adicionarLinhaItem}
                        >
                          <Plus size={14} /> Adicionar item
                        </button>
                      </div>

                      {novoItens.map((item, index) => (
                        <div key={index} className={styles.itemLinha}>
                          <div className={styles.itemLinhaTopo}>
                            <strong>Item {index + 1}</strong>
                            {novoItens.length > 1 && (
                              <button
                                type="button"
                                className={styles.btnRemoverItem}
                                onClick={() => removerLinhaItem(index)}
                              >
                                <X size={14} />
                              </button>
                            )}
                          </div>
                          <div className={styles.itemLinhaCampos}>
                            <input
                              placeholder="Número do patrimônio *"
                              value={item.numero_patrimonio}
                              onChange={(e) =>
                                atualizarItem(
                                  index,
                                  "numero_patrimonio",
                                  e.target.value,
                                )
                              }
                            />
                            <input
                              placeholder="Nome do item *"
                              value={item.nome}
                              onChange={(e) =>
                                atualizarItem(index, "nome", e.target.value)
                              }
                            />
                            <input
                              placeholder="Categoria"
                              value={item.categoria}
                              onChange={(e) =>
                                atualizarItem(
                                  index,
                                  "categoria",
                                  e.target.value,
                                )
                              }
                            />
                            <input
                              placeholder="Número de série"
                              value={item.numero_serie}
                              onChange={(e) =>
                                atualizarItem(
                                  index,
                                  "numero_serie",
                                  e.target.value,
                                )
                              }
                            />
                            <input
                              type="number"
                              min={1}
                              placeholder="Qtd"
                              value={item.quantidade}
                              onChange={(e) =>
                                atualizarItem(
                                  index,
                                  "quantidade",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <footer className={styles.modalFooter}>
                  <button
                    className={styles.btnOutline}
                    onClick={fecharModalNovo}
                  >
                    Cancelar
                  </button>
                  <button
                    className={styles.btnPrimary}
                    onClick={criarPatrimonio}
                  >
                    Criar
                  </button>
                </footer>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal — Detalhe do Patrimônio (Case ou Unitario) */}
      {patrimonioAtual && (
        <div className={styles.overlay} onClick={fecharDetalhe}>
          <div
            className={`${styles.modal} ${styles.modalGrande}`}
            onClick={(e) => e.stopPropagation()}
          >
            <header className={styles.modalHeader}>
              <div>
                {pilha.length > 1 && (
                  <button
                    className={styles.breadcrumbBtn}
                    onClick={voltarDetalhe}
                  >
                    ← Voltar para {pilha[pilha.length - 2].nome}
                  </button>
                )}
                <p className={styles.eyebrow}>
                  {patrimonioAtual.numero_patrimonio} ·{" "}
                  {patrimonioAtual.projeto}
                </p>
                <h2>{patrimonioAtual.nome}</h2>
              </div>

              <button className={styles.btnCloseModal} onClick={fecharDetalhe}>
                <X size={20} />
              </button>
            </header>

            <div className={styles.modalBody}>
              <div className={styles.detalheTopo}>
                <div className={styles.detalheTopoTags}>
                  <TipoBadge tipo={patrimonioAtual.tipo_patrimonio} />
                  <StatusBadge
                    status={patrimonioAtual.status_atual}
                    localizacao={patrimonioAtual.localizacao_atual}
                  />
                </div>
                <div className={styles.detalheAcoes}>
                  <button className={styles.btnOutline} onClick={abrirModalMov}>
                    <ArrowRightLeft size={14} /> Registrar movimentação
                  </button>
                  {patrimonioAtual.tipo_patrimonio === "Unitario" &&
                    (patrimonioAtual.id_patrimonio_pai ? (
                      <button
                        className={styles.btnOutline}
                        onClick={desassociar}
                      >
                        <Unlink size={14} /> Remover do grupo
                      </button>
                    ) : (
                      <button
                        className={styles.btnOutline}
                        onClick={abrirAssociar}
                      >
                        <Link2 size={14} /> Adicionar a uma case
                      </button>
                    ))}
                </div>
              </div>

              {patrimonioAtual.id_patrimonio_pai && (
                <div className={styles.parentBanner}>
                  Está dentro da case{" "}
                  <button
                    className={styles.parentBannerLink}
                    onClick={() =>
                      abrirDetalhe(
                        patrimonioAtual.id_patrimonio_pai as number,
                        false,
                      )
                    }
                  >
                    {patrimonioAtual.numero_patrimonio_pai} ·{" "}
                    {patrimonioAtual.nome_pai}
                  </button>
                </div>
              )}

              <div className={styles.detailsGrid}>
                {(() => {
                  const localizacaoFisica = STATUS_LOCALIZACAO_FISICA.has(
                    patrimonioAtual.status_atual,
                  );
                  return (
                    <>
                      <div>
                        <span className={styles.infoLabel}>
                          Localização atual
                        </span>
                        <p>
                          {localizacaoFisica
                            ? patrimonioAtual.localizacao_atual || "—"
                            : (STATUS_LABEL[patrimonioAtual.status_atual] ??
                              patrimonioAtual.status_atual)}
                        </p>
                      </div>
                      {!localizacaoFisica && (
                        <div>
                          <span className={styles.infoLabel}>
                            Localização anterior
                          </span>
                          <p>{patrimonioAtual.localizacao_atual || "—"}</p>
                        </div>
                      )}
                    </>
                  );
                })()}
                <div>
                  <span className={styles.infoLabel}>Com quem está</span>
                  <p>{patrimonioAtual.com_quem_esta || "—"}</p>
                </div>
                <div>
                  <span className={styles.infoLabel}>Categoria</span>
                  <p>{patrimonioAtual.categoria || "—"}</p>
                </div>
                {patrimonioAtual.tipo_patrimonio === "Unitario" && (
                  <div>
                    <span className={styles.infoLabel}>Número de série</span>
                    <p>{patrimonioAtual.numero_serie || "—"}</p>
                  </div>
                )}
              </div>

              {patrimonioAtual.tipo_patrimonio === "Case" && (
                <div className={styles.fullWidthBlock}>
                  <span className={styles.infoLabel}>
                    Itens dentro desta case
                  </span>
                  {!patrimonioAtual.itens ||
                  patrimonioAtual.itens.length === 0 ? (
                    <p className={styles.textoMuted}>
                      Nenhum item associado no momento.
                    </p>
                  ) : (
                    <table className={styles.tabelaItens}>
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Nº patrimônio</th>
                          <th>Nº série</th>
                          <th>Status</th>
                          <th>Com quem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {patrimonioAtual.itens.map((item) => (
                          <tr
                            key={item.id}
                            className={styles.itemClicavel}
                            onClick={() => abrirDetalhe(item.id, false)}
                          >
                            <td>{item.nome}</td>
                            <td>{item.numero_patrimonio}</td>
                            <td>{item.numero_serie || "—"}</td>
                            <td>
                              <StatusBadge
                                status={item.status_atual}
                                localizacao={item.localizacao_atual}
                              />
                            </td>
                            <td>{item.com_quem_esta || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              <div className={styles.fullWidthBlock}>
                <span className={styles.infoLabel}>
                  Histórico de movimentações
                </span>
                {!patrimonioAtual.movimentacoes ||
                patrimonioAtual.movimentacoes.length === 0 ? (
                  <p className={styles.textoMuted}>
                    Nenhuma movimentação registrada.
                  </p>
                ) : (
                  <div className={styles.timeline}>
                    {patrimonioAtual.movimentacoes.map((mov) => {
                      const descricaoGrupo = descreverMovimentacaoGrupo(mov);
                      const localizacaoMudou =
                        mov.localizacao_origem &&
                        mov.localizacao_destino &&
                        mov.localizacao_origem !== mov.localizacao_destino;
                      return (
                        <div key={mov.id} className={styles.timelineItem}>
                          <div className={styles.timelinePonto} />
                          <div className={styles.timelineConteudo}>
                            <div className={styles.timelineTopo}>
                              <strong>{mov.tipo_movimentacao}</strong>
                              <span>
                                {new Date(mov.data_movimentacao).toLocaleString(
                                  "pt-BR",
                                )}
                              </span>
                            </div>
                            {mov.status_anterior &&
                              mov.status_anterior !== mov.status_novo && (
                                <p>
                                  {STATUS_LABEL[mov.status_anterior] ??
                                    mov.status_anterior}{" "}
                                  →{" "}
                                  {STATUS_LABEL[mov.status_novo] ??
                                    mov.status_novo}
                                </p>
                              )}
                            {descricaoGrupo && (
                              <p className={styles.timelineDetalheDestaque}>
                                {descricaoGrupo}
                              </p>
                            )}
                            {(mov.retirado_por || mov.com_quem_esta) && (
                              <p className={styles.timelineDetalhe}>
                                {mov.retirado_por &&
                                  `Retirado/instalado por: ${mov.retirado_por}`}
                                {mov.retirado_por && mov.com_quem_esta && " · "}
                                {mov.com_quem_esta &&
                                  `Com: ${mov.com_quem_esta}`}
                              </p>
                            )}
                            {localizacaoMudou ? (
                              <p className={styles.timelineDetalheDestaque}>
                                {mov.localizacao_origem} →{" "}
                                {mov.localizacao_destino}
                              </p>
                            ) : (
                              mov.localizacao_destino && (
                                <p className={styles.timelineDetalhe}>
                                  Local: {mov.localizacao_destino}
                                </p>
                              )
                            )}
                            {mov.observacoes && (
                              <p className={styles.timelineDetalhe}>
                                {mov.observacoes}
                              </p>
                            )}
                            <span className={styles.timelineUsuario}>
                              Registrado por {mov.nome_usuario_registro || "—"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal — Nova Movimentação */}
      {modalMovShow && patrimonioAtual && (
        <div className={styles.overlay} onClick={() => setModalMovShow(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <header className={styles.modalHeader}>
              <h2>Registrar movimentação</h2>
              <button
                className={styles.btnCloseModal}
                onClick={() => setModalMovShow(false)}
              >
                <X size={20} />
              </button>
            </header>
            <div className={styles.modalBody}>
              <p className={styles.textoMuted}>
                {patrimonioAtual.numero_patrimonio} · {patrimonioAtual.nome}
              </p>

              <label>Tipo de movimentação</label>
              <select
                value={novaMov.tipo_movimentacao}
                onChange={(e) => {
                  const novoTipoMov = e.target.value;
                  setNovaMov((f) => ({
                    ...f,
                    tipo_movimentacao: novoTipoMov,
                    retirado_por: RESPONSAVEL_LABEL[novoTipoMov]
                      ? f.retirado_por
                      : "",
                  }));
                }}
              >
                <option value="Retirada">Retirada</option>
                <option value="Devolucao">Devolução</option>
                <option value="Manutencao">Manutenção</option>
                <option value="Instalacao">Instalação</option>
                <option value="Realocacao">Realocação</option>
                <option value="Guarda">Guarda</option>
                <option value="Desativacao">Desativação</option>
              </select>

              <label>Novo status</label>
              <select
                value={novaMov.status_novo}
                onChange={(e) =>
                  setNovaMov((f) => ({ ...f, status_novo: e.target.value }))
                }
              >
                {STATUS_OPCOES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </select>

              {RESPONSAVEL_LABEL[novaMov.tipo_movimentacao] && (
                <>
                  <label>{RESPONSAVEL_LABEL[novaMov.tipo_movimentacao]}</label>
                  <input
                    value={novaMov.retirado_por}
                    onChange={(e) =>
                      setNovaMov((f) => ({
                        ...f,
                        retirado_por: e.target.value,
                      }))
                    }
                    placeholder={
                      RESPONSAVEL_PLACEHOLDER[novaMov.tipo_movimentacao] ??
                      "Nome do responsável"
                    }
                  />
                </>
              )}

              <label>Com quem está agora</label>
              <input
                value={novaMov.com_quem_esta}
                onChange={(e) =>
                  setNovaMov((f) => ({ ...f, com_quem_esta: e.target.value }))
                }
                placeholder="Pessoa ou setor responsável"
              />

              <label>Localização de destino</label>
              <input
                value={novaMov.localizacao_destino}
                onChange={(e) =>
                  setNovaMov((f) => ({
                    ...f,
                    localizacao_destino: e.target.value,
                  }))
                }
                placeholder="Ex: Loja 123, Assistência técnica X"
              />

              <label>Previsão de retorno</label>
              <input
                type="date"
                value={novaMov.data_prevista_retorno}
                onChange={(e) =>
                  setNovaMov((f) => ({
                    ...f,
                    data_prevista_retorno: e.target.value,
                  }))
                }
              />

              <label>Observações</label>
              <textarea
                rows={3}
                value={novaMov.observacoes}
                onChange={(e) =>
                  setNovaMov((f) => ({ ...f, observacoes: e.target.value }))
                }
              />
            </div>
            <footer className={styles.modalFooter}>
              <button
                className={styles.btnOutline}
                onClick={() => setModalMovShow(false)}
              >
                Cancelar
              </button>
              <button
                className={styles.btnPrimary}
                onClick={registrarMovimentacao}
              >
                Registrar
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Modal — Associar a uma Case */}
      {modalAssociarShow && patrimonioAtual && (
        <div
          className={styles.overlay}
          onClick={() => setModalAssociarShow(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <header className={styles.modalHeader}>
              <h2>Adicionar a uma case</h2>
              <button
                className={styles.btnCloseModal}
                onClick={() => setModalAssociarShow(false)}
              >
                <X size={20} />
              </button>
            </header>
            <div className={styles.modalBody}>
              <p className={styles.textoMuted}>
                {patrimonioAtual.numero_patrimonio} · {patrimonioAtual.nome}
              </p>

              <label>Case de destino *</label>
              <select
                value={caseSelecionada}
                onChange={(e) => setCaseSelecionada(e.target.value)}
              >
                <option value="">Selecione...</option>
                {casesDisponiveis.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.numero_patrimonio} · {c.nome}
                  </option>
                ))}
              </select>
              {casesDisponiveis.length === 0 && (
                <p className={styles.textoMuted}>
                  Nenhuma case cadastrada ainda. Crie uma case primeiro.
                </p>
              )}

              <label>Observações</label>
              <textarea
                rows={2}
                value={obsAssociar}
                onChange={(e) => setObsAssociar(e.target.value)}
                placeholder="Ex: reposição de peça, retirado da pilha de avulsos"
              />
            </div>
            <footer className={styles.modalFooter}>
              <button
                className={styles.btnOutline}
                onClick={() => setModalAssociarShow(false)}
              >
                Cancelar
              </button>
              <button className={styles.btnPrimary} onClick={confirmarAssociar}>
                Adicionar
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default Patrimonios;
